import { Inject, Injectable } from '@angular/core';
import { EnvConfig, ErrorKind, PageType, RegisterForm, SnackbarType, Token } from '@shared/definitions';
import { ENV_CONFIG } from '@digilize/shared/utils/tokens';
import { DialogService } from '@digilize/shared/utils/services';
import { AuthApiService } from '@digilize/shared/data/services';
import { Router } from '@angular/router';
import { Alert, AlertService, AlertType } from '@digilize/shared/feature/modules/alert';
import {
  AuthService,
  DialogPagesComponent,
  DialogUpdateFbDetailsComponent,
} from '@digilize/shared/feature/modules/auth';
import { forkJoin } from 'rxjs';
import { first, switchMap } from 'rxjs/operators';
import { TranslocoService } from '@ngneat/transloco';
import { SnackbarService } from '@digilize/shared/feature/modules/snackbar';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { getCaptchaHeaders } from '@digilize/shared/utils/helpers';

@Injectable({
  providedIn: 'root'
})
export class  FacebookAuthService {
  private readonly FB_AUTHORIZE_URL = 'https://graph.facebook.com/oauth/authorize';

  constructor(@Inject(ENV_CONFIG) private envConfig: EnvConfig,
              private authApiService: AuthApiService,
              private router: Router,
              private dialogService: DialogService,
              private alertService: AlertService,
              private translocoService: TranslocoService,
              private authService: AuthService,
              private snackbarService: SnackbarService,
              private recaptchaV3Service: ReCaptchaV3Service) {
  }

  facebookRedirect(): void {
    const isAdminPanel = window.location.href.includes('/admin');
    const client = this.envConfig.facebookAppId;
    // For facebook redirect_uri
    const redirectUrl = encodeURIComponent(window.location.origin + (isAdminPanel ? '/admin' : '') + '/');
    const returnUrl = window.location.pathname; // For redirect after success login
    const scope = 'public_profile,email';

    localStorage.setItem('redirectUrl', redirectUrl);
    localStorage.setItem('returnUrl', returnUrl);

    window.location.href = `${this.FB_AUTHORIZE_URL}?client_id=${client}&redirect_uri=${redirectUrl}&scope=${scope}`;
  }

  pagesRedirect(shopId: string, pageType: PageType): void {
    const client = this.envConfig.facebookAppId;
    const redirectUrl = encodeURIComponent(window.location.origin + '/admin');
    const returnUrl = window.location.pathname;
    const { scope, state } = this.makeFacebookAuthorizeParams(pageType);

    localStorage.setItem('shopId', shopId);
    localStorage.setItem('redirectUrl', redirectUrl);
    localStorage.setItem('returnUrl', returnUrl);

    window.location.href = `${this.FB_AUTHORIZE_URL}?client_id=${client}&redirect_uri=${redirectUrl}&scope=${scope}&state=${state}`;
  }

  handleFbPages(code: string, pageType: PageType): void {
    const redirectUrl = localStorage.getItem('redirectUrl');
    const returnUrl = localStorage.getItem('returnUrl').replace('/admin', '');
    const shopId = localStorage.getItem('shopId');
    const alertMessageTextSuccess$ = this.translocoService
      .selectTranslate('social_media_connection.some_shops_no_longer_available')
      .pipe(first());
    forkJoin([this.authApiService.getPages(pageType, code, redirectUrl), alertMessageTextSuccess$]).subscribe(
      {
        next:   ([response, successText]) => {
          this.dialogService.openDialog(DialogPagesComponent, {
            data: {
              pages: response.data,
              shopId: shopId,
              pageType: pageType,
            },
          });

          if (response.warning.type === 'facebook_revoked') {
            const alert: Alert = {
              type: AlertType.Warning,
              title: {
                message: 'Warning',
                translate: false,
              },
              message: {
                message: successText,
                translate: false,
              },
            };
            this.alertService.setAlert(alert);
          }
        },
        error: (error) => {
          if (error.error?.error?.info?.kind === ErrorKind.INSTAGRAM_PAGE_ID_NOT_FOUND) {
            // No connection between selected Business Account and page
            this.translocoService
              .selectTranslate('social_media_connection.no_business_connection')
              .subscribe((textFail) => {
                const alert: Alert = {
                  type: AlertType.Warning,
                  title: {
                    message: 'Warning',
                    translate: false,
                  },
                  message: {
                    message: textFail,
                    translate: false,
                  },
                };
                this.alertService.setAlert(alert);
              });
          } else {
            this.alertService.handleApiError(error.error.error);
          }
          this.router.navigate([returnUrl]);
        }
      },
    );
  }

  handleFbCode(params: { code: string }, app: string): void {
    if (params.code) {
      const code = params.code;
      const redirectUrl = localStorage.getItem('redirectUrl');
      const returnUrl = localStorage.getItem('returnUrl');
      const updateDetails = JSON.parse(localStorage.getItem('updateDetails'));

      if (updateDetails) {
        const registerForm: RegisterForm = {
          email: updateDetails.email,
          first_name: updateDetails.first_name,
          last_name: updateDetails.last_name,
          password: null,
          password_confirm: null,
          facebook_code: params.code,
          facebook_redirect_uri: decodeURIComponent(localStorage.getItem('redirectUrl')),
        };
        localStorage.removeItem('updateDetails');
        this.recaptchaV3Service
          .execute('register')
          .pipe(
            switchMap((captchaToken) => {
              return this.authApiService.register(
                registerForm,
                getCaptchaHeaders(captchaToken, this.envConfig.recaptchaSiteKey)
              );
            })
          )
          .subscribe(
            {
              next:  (token: Token) => {
                this.authService.setAcquisitionSession(token, redirectUrl, returnUrl, app);
              },
              error: (error) => {
                if (error.error.error.type === ErrorKind.CAPTCHA_INVALID) {
                  this.snackbarService.openSnackbar(SnackbarType.Error, {
                    duration: 15000,
                    data: { msg: 'alert.custom.seems_you_are_bot', translate: true },
                  });
                }
                if (
                  error.error.error.type === ErrorKind.VALIDATION_ERROR &&
                  error.error.error.info?.kind === ErrorKind.ALREADY_EXISTS
                ) {
                  this.dialogService.openDialog(DialogUpdateFbDetailsComponent, {
                    data: {
                      error: 'user_already_exist',
                      ...registerForm,
                    },
                  });
                }
              }
            }
          );
      } else {
        this.authApiService.fbLogin(code, redirectUrl).subscribe(
          {
            next:  (token: Token) => {
              this.authService.setAcquisitionSession(token, redirectUrl, returnUrl, app);
            },
            error: (error) => {
              if (error.error.error.type === ErrorKind.VALIDATION_ERROR) {
                this.dialogService.openDialog(DialogUpdateFbDetailsComponent);
              } else {
                window.location.href = '/';
              }
            }
          }
        );
      }
    }
  }

  private makeFacebookAuthorizeParams(pageType: PageType): { scope: string, state: string} {
    if (pageType === PageType.Facebook) {
      return {
        scope: 'public_profile,pages_show_list,pages_manage_posts',
        state: 'pages_facebook',
      };
    }

    return {
      scope: 'public_profile,pages_show_list,pages_manage_posts,instagram_basic,instagram_content_publish',
      state: 'pages_instagram'
    }
  }

}
